import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo, useCallback, useMemo } from "react";
import { FilterSection } from "./FilterSection";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface SupplierFilterListProps {
  /**
   * All available seller carts
   */
  carts: StoreSellerCart[];
  /**
   * IDs of currently selected carts
   */
  selectedCartIds: string[];
  /**
   * Callback when toggling a cart's selection
   */
  onToggleCart: (cartId: string) => void;
}

/**
 * Collapsible "Suppliers" filter section.
 *
 * Mirrors the search page's `FilterSection`: a section title with a rotating
 * chevron, then plain checkbox rows of `name ... (count)` - rather than the
 * bordered cards this used previously - so both filter rails read the same.
 */
function SupplierFilterListComponent({
  carts,
  selectedCartIds,
  onToggleCart
}: SupplierFilterListProps) {
  const sortedCarts = useMemo(
    () => [...carts].sort((a, b) => a.seller.name.localeCompare(b.seller.name)),
    [carts]
  );

  if (carts.length === 0) return null;

  return (
    <FilterSection title="Suppliers">
      {sortedCarts.map((cart) => {
        const isChecked = selectedCartIds.includes(cart.cart_id);
        const itemCount = cart.items.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        return (
          <SupplierFilterItem
            key={cart.cart_id}
            cartId={cart.cart_id}
            sellerName={cart.seller.name}
            itemCount={itemCount}
            isChecked={isChecked}
            onToggle={onToggleCart}
          />
        );
      })}
    </FilterSection>
  );
}

interface SupplierFilterItemProps {
  cartId: string;
  sellerName: string;
  itemCount: number;
  isChecked: boolean;
  onToggle: (cartId: string) => void;
}

/**
 * Individual supplier filter checkbox row.
 * Memoized to prevent unnecessary re-renders.
 */
const SupplierFilterItem = memo(function SupplierFilterItem({
  cartId,
  sellerName,
  itemCount,
  isChecked,
  onToggle
}: SupplierFilterItemProps) {
  const handleToggle = useCallback(() => {
    onToggle(cartId);
  }, [cartId, onToggle]);

  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm">
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleToggle}
        className="mt-0.5 h-4 w-4 rounded border-gray-300"
      />
      <span className="flex-1">{sellerName}</span>
      <span className="text-xs text-gray-400">({itemCount})</span>
    </label>
  );
});

export const SupplierFilterList = memo(SupplierFilterListComponent);
